import {Component, Input, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {SortTableRolePicker} from 'src/shared/models/sort-table/cell-classes';
import {BaseCellComponent} from '../_base-cell/base-cell.component';
import {PermissionsService} from 'src/core/services/permissions.service';
import {PillMultiselectComponent} from '../../../pill-multiselect/pill-multiselect.component';
import {RoleOptions, Role, rolesToPillOptions, PillOption} from 'src/shared/models/fga/role';
import { Subject } from 'rxjs';
import { ShadeToolsService } from 'src/core/services/shade-tools.service';
import { extractErrorMessage } from 'src/shared/utils/error-utils';
import { SessionService } from 'src/core/services/session.service';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import { ShareService } from 'src/moti/services/share.service';

@Component({
  selector: 'motn-role-picker-cell',
  imports: [
    PillMultiselectComponent,
  ],
  templateUrl: './role-picker-cell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './role-picker-cell.component.scss',
})
export class RolePickerCellComponent extends BaseCellComponent {
  @Input() cell: SortTableRolePicker;
  @ViewChild('pillMultiselect') pillMultiselect: PillMultiselectComponent;

  RoleOptions = RoleOptions;
  selectedRoleOptions: PillOption<Role>[] = [];
  rolePickerOpenMenu$ = new Subject<void>();

  ngOnInit() {
    this.setRolesAndUpdatePills(this.cell.preselectedRoles || []);
  }

  private setRolesAndUpdatePills(roles: Role[]) {
    this.cell.preselectedRoles = roles;
    this.selectedRoleOptions = rolesToPillOptions(roles);
  }

  constructor(
    private permissionsService: PermissionsService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
    private shareService: ShareService) {
    super();
  }

  onAddRole(option: PillOption<Role>) {
    const newRoles = [...(this.cell.preselectedRoles || []), option.key];
    this.setRolesAndUpdatePills(newRoles);
    this.permissionsService.assignRolesToUser({
      UserID: this.cell.userId,
      ObjectID: this.cell.object.id,
      ObjectType: this.cell.object.type,
      Roles: [option.key],
    }).subscribe({
      error: (err) => {
        this.setRolesAndUpdatePills(newRoles.filter(r => r !== option.key));
        this.shadeTools.errorDialog(extractErrorMessage(err, 'Failed to add role. Please try again.'));
      }
    });
  }

  onRemoveRole(option: PillOption<Role>) {
    // Check if user is removing their own admin role
    const isRemovingOwnAdminRole =
      option.key === Role.ADMIN &&
      this.cell.userId === this.session.userId;

    if (isRemovingOwnAdminRole) {
      // Store current roles to restore if user cancels
      const currentRoles = [...(this.cell.preselectedRoles || [])];

      // Close the dropdown menu so it doesn't cover the dialog
      this.pillMultiselect?.closeMenu();

      // Show confirmation dialog
      this.shadeTools.startDialog(
        '<span class="red"><i class="far fa-exclamation-triangle"></i> Remove Your Admin Role?</span>',
        'By removing the Admin role from your own user, you may not be able to manage shares anymore for this item. Are you sure?',
        [
          {
            text: 'Cancel',
            callback: () => {
              // Restore the roles to original state
              this.setRolesAndUpdatePills(currentRoles);
            },
            color: DialogBoxButtonColor.Default,
          },
          {
            text: 'Remove Admin Role',
            callback: () => {
              // Close share manager immediately for better UX
              this.shareService.showShareManagerForObject$.next(null);
              // Proceed with removal in the background
              this.performRoleRemoval(option, false);
            },
            color: DialogBoxButtonColor.Red,
          }
        ],
        false,
        false,
        false,
        true
      );
    } else {
      // Not removing own admin role - proceed normally
      this.performRoleRemoval(option);
    }
  }

  private performRoleRemoval(option: PillOption<Role>, closeShareManagerOnSuccess: boolean = false) {
    const newRoles = (this.cell.preselectedRoles || []).filter(r => r !== option.key);

    this.setRolesAndUpdatePills(newRoles);
    this.permissionsService.unassignRolesFromUser({
      UserID: this.cell.userId,
      ObjectID: this.cell.object.id,
      ObjectType: this.cell.object.type,
      Roles: [option.key],
    }).subscribe({
      next: () => {
        // If removing own admin role, close the share manager
        if (closeShareManagerOnSuccess) {
          this.shareService.showShareManagerForObject$.next(null);
        }
      },
      error: (err) => {
        this.setRolesAndUpdatePills([...newRoles, option.key]);
        this.shadeTools.errorDialog(extractErrorMessage(err, 'Failed to remove role. Please try again.'));
      }
    });
  }

  triggerRolePickerMenu() {
    this.rolePickerOpenMenu$.next();
  }
}
